import React, { useMemo, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreIcon from '@mui/icons-material/MoreVert';
import Tooltip from '@mui/material/Tooltip';
import { useSelector } from 'react-redux';
import { useStyles } from './index.styles';
import { Box, Stack } from '@mui/material';
import UserService from '@api/Users/UserCtrl';
import { showAlertError } from '@utils/Alerts/Error';
import { hideLoader, showLoader } from '@utils/Loader';
import { RootReducer } from '@store/index';
import { useNavigate } from 'react-router-dom';
import CustomBreadcrumbs from '@components/Utilities/CustomBreadcrumbs';
import BranchOfficeName from '@components/Utilities/Navbar/BranchOfficeName';
import PreviewModeBanner from '@components/Utilities/Navbar/PreviewModeBanner';
import { getProfileName } from '@utils/Profiles';
import OnlineAvatar from '@components/Utilities/Avatars/OnlineAvatar';
import CashRegisterTime from '@components/Utilities/Navbar/CashRegisterTime';
import NotificationsBell from '@components/Utilities/Navbar/NotificationsBell';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { isActiveCashRegister } from '@utils/CashRegisters';

interface NavbarProps {
    handleDrawerToggle: () => void;
}

const Navbar: React.FunctionComponent<NavbarProps> = (props) => {
    const { handleDrawerToggle } = props;
    const navigation = useNavigate();
    const { loggedUser } = useSelector((state: RootReducer) => state.auth);
    const classes = useStyles();
    const userService = useMemo(() => new UserService(), []);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
    const { cashRegister } = useSelector((state: RootReducer) => state.krispis);
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);

    const onViewProfile = () => {
        navigation('myProfile');
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMobileMenuOpen = (event: any) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const doLogout = async () => {
        showLoader();
        try {
            await userService.onLogout();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    /**
     * Antes esto era un menú desplegable con UNA sola opción, "Cerrar sesión":
     * dos clics para lo mismo y un icono de flecha que no anunciaba nada.
     * Ahora el botón dice qué hace y la red de seguridad es la confirmación
     * (2026-08-29, pedido de Irving).
     *
     * ⚠️ **Avisa si deja la caja abierta.** Cerrar sesión NO cierra la caja, y ésa
     * es una de las formas en que se acumulan cajas abiertas de días anteriores
     * —32 al 29-ago—: el turno termina, la sesión se va y nadie hace el corte.
     */
    const onLogoutPress = () => {
        handleMobileMenuClose();
        const cajaAbierta = isActiveCashRegister(cashRegister, branchOfficeSelected?.id);
        showIOSAlert({
            title: 'Cerrar sesión',
            message: cajaAbierta
                ? 'Tienes una CAJA ABIERTA y cerrar sesión no la cierra: quedará sin corte.\n\n¿Cerrar sesión de todos modos?'
                : '¿Quieres cerrar tu sesión?',
            buttons: [
                { text: 'Cerrar sesión', onPress: doLogout, style: 'destructive' },
                { text: 'Cancelar', onPress: () => {}, style: 'cancel' }
            ]
        });
    };

    const mobileMenuId = 'primary-search-account-menu-mobile';
    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            <MenuItem
                onClick={() => {
                    handleMobileMenuClose();
                    onViewProfile();
                }}
            >
                Mi perfil
            </MenuItem>
            <MenuItem onClick={onLogoutPress}>Cerrar sesión</MenuItem>
        </Menu>
    );

    return (
        <Box>
            <PreviewModeBanner />
            <AppBar position="static" elevation={0}>
                <Toolbar style={{ height: '70px' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <IconButton
                            color="secondary"
                            edge="start"
                            aria-label="open drawer"
                            onClick={handleDrawerToggle}
                        >
                            <MenuIcon />
                        </IconButton>
                        <CustomBreadcrumbs />
                    </Stack>
                    <div className={classes.grow} />
                    <div className={classes.sectionDesktop}>
                        {/* ⚠️ **Un solo `spacing` para TODOS los bloques** (2026-08-29,
                            pedido de Irving: "se ve raro"). Antes eran tres ritmos
                            distintos —4 entre bloques, 2 dentro del usuario, 0.5
                            entre los iconos— y la barra se leía como piezas sueltas.
                            Los iconos son hijos DIRECTOS de este Stack, no un grupo
                            aparte: así todo tiene exactamente la misma separación. */}
                        <Stack direction="row" spacing={3} alignItems="center">
                            <BranchOfficeName />
                            <CashRegisterTime />
                            <Stack
                                direction="row"
                                alignItems="center"
                                sx={{ cursor: 'pointer' }}
                                onClick={onViewProfile}
                                spacing={1.5}
                            >
                                <Stack alignItems="flex-end">
                                    <Typography variant="body1" fontWeight={600}>
                                        {loggedUser?.profile.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        {getProfileName(loggedUser?.profile.profile)}
                                    </Typography>
                                </Stack>
                                <OnlineAvatar alt={loggedUser?.profile.name} src={loggedUser?.profile.photo} />
                            </Stack>
                            <NotificationsBell />
                            <Tooltip title="Cerrar sesión">
                                <IconButton
                                    color="secondary"
                                    edge="end"
                                    aria-label="Cerrar sesión"
                                    onClick={onLogoutPress}
                                >
                                    <LogoutIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </div>
                    <div className={classes.sectionMobile}>
                        <IconButton
                            aria-label="show more"
                            aria-controls={mobileMenuId}
                            aria-haspopup="true"
                            onClick={handleMobileMenuOpen}
                            color="inherit"
                        >
                            <MoreIcon />
                        </IconButton>
                    </div>
                </Toolbar>
            </AppBar>
            {renderMobileMenu}
        </Box>
    );
};

export default Navbar;
